import React from "react";
import { COLOR_MODS, SIZE_MODS, ACTIONS, PRONOUN_ENTITIES } from "../../lib/iloSitelen";
import { GLYPHS } from "../../lib/iloSitelenGlyphs";

/* ============================================================
   SceneRenderer — draws an ilo-sitelen Scene (see src/lib/iloSitelen.js
   for the Ent/Scene shapes) as a pure-SVG illustration. No external
   libraries; all motion is plain CSS keyframe animation on <g> groups.
   ============================================================ */

const STAGE_BG = "#f7f3ea";
const GROUND = "#e4dccb";
const DETAIL_DARK = "#33323b";
const NEG_RED = "#d95d43";
const DEFAULT_BODY = "#b9a88f";
const GROUND_Y = 250;

const entColor = (ent) => (ent && ent.color && COLOR_MODS[ent.color]) || DEFAULT_BODY;
const entScale = (ent) => (ent && ent.size && SIZE_MODS[ent.size]) || 1;

// Fanned offsets for count 1/2/3 — small scale + horizontal spread so
// grouped entities read as a cluster rather than overlapping exactly.
const FAN = {
  1: [{ dx: 0, s: 1 }],
  2: [{ dx: -20, s: 0.9 }, { dx: 18, s: 1 }],
  3: [{ dx: -28, s: 0.82 }, { dx: 0, s: 1 }, { dx: 26, s: 0.88 }],
};

/* ---------------- low-level glyph rendering ---------------- */

// Renders one glyph's shape list. `color` fills "body" areas; "detail"
// areas are dark, except when the body color is pimeja (near-black),
// in which case detail flips to white so eyes/marks stay visible.
function Glyph({ shapes, color }) {
  const detail = color === COLOR_MODS.pimeja ? "#fff" : DETAIL_DARK;
  const fillFor = (s) => (s.f === "body" ? color : s.f === "detail" ? detail : "none");
  const strokeFor = (s) => {
    if (s.s === "detail") return detail;
    if (s.s === "none") return "none";
    return s.t === "line" ? detail : "none";
  };
  return (
    <>
      {(shapes || []).map((s, i) => {
        const fill = fillFor(s);
        const stroke = strokeFor(s);
        const sw = s.t === "rect" ? s.w2 : s.w;
        const strokeWidth = sw || 1.5;
        switch (s.t) {
          case "path":
            return <path key={i} d={s.d} fill={fill} stroke={stroke} strokeWidth={strokeWidth} />;
          case "circle":
            return <circle key={i} cx={s.cx} cy={s.cy} r={s.r} fill={fill} stroke={stroke} strokeWidth={strokeWidth} />;
          case "ellipse":
            return <ellipse key={i} cx={s.cx} cy={s.cy} rx={s.rx} ry={s.ry} fill={fill} stroke={stroke} strokeWidth={strokeWidth} />;
          case "rect":
            return <rect key={i} x={s.x} y={s.y} width={s.w} height={s.h} rx={s.rx} fill={fill} stroke={stroke} strokeWidth={strokeWidth} />;
          case "line":
            return <line key={i} x1={s.x1} y1={s.y1} x2={s.x2} y2={s.y2} stroke={stroke} strokeWidth={strokeWidth} />;
          default:
            return null;
        }
      })}
    </>
  );
}

function Diamond({ x, y, s }) {
  return <path d={`M ${x} ${y - s} L ${x + s} ${y} L ${x} ${y + s} L ${x - s} ${y} Z`} fill="#e5b93c" opacity="0.9" />;
}

// Note: positioning (`transform`) and CSS animation live on separate
// nested <g>s — a CSS `transform` on an element overrides its SVG
// `transform` attribute rather than composing with it.
function Heart({ x, y, scale = 1, delay = "0s", color = "#e8749a", className = "ilo-rise-item" }) {
  return (
    <g transform={`translate(${x},${y}) scale(${scale})`}>
      <g className={className} style={{ animationDelay: delay, transformBox: "fill-box", transformOrigin: "center" }}>
        <path d="M0 4 C -6 -4 -14 2 0 12 C 14 2 6 -4 0 4 Z" fill={color} />
      </g>
    </g>
  );
}

function Star({ x, y, scale = 1, delay = "0s" }) {
  return (
    <g transform={`translate(${x},${y}) scale(${scale})`}>
      <g className="ilo-sparkle" style={{ animationDelay: delay, transformBox: "fill-box", transformOrigin: "center" }}>
        <path d="M0 -8 L2 -2 L8 0 L2 2 L0 8 L-2 2 L-8 0 L-2 -2 Z" fill="#e5b93c" />
      </g>
    </g>
  );
}

/* ---------------- mood / badge overlays ---------------- */

// Small decoration near an entity's head reflecting scene mood words.
function MoodOverlay({ mood, cx, top }) {
  if (!mood) return null;
  if (mood === "pona") {
    return (
      <g>
        <path d={`M ${cx - 6} ${top + 6} Q ${cx} ${top + 11} ${cx + 6} ${top + 6}`} fill="none" stroke={DETAIL_DARK} strokeWidth="1.5" strokeLinecap="round" />
        <Diamond x={cx - 14} y={top - 6} s={3} />
        <Diamond x={cx + 14} y={top - 2} s={2.4} />
      </g>
    );
  }
  if (mood === "ike") {
    return (
      <g className="ilo-mood-cloud">
        <ellipse cx={cx} cy={top - 4} rx={14} ry={7} fill="#8b8b95" />
        <ellipse cx={cx - 8} cy={top} rx={9} ry={5.5} fill="#8b8b95" />
        <ellipse cx={cx + 8} cy={top} rx={9} ry={5.5} fill="#8b8b95" />
        <line x1={cx - 6} y1={top + 8} x2={cx - 9} y2={top + 14} stroke="#4a6fa5" strokeWidth="1.5" />
        <line x1={cx + 4} y1={top + 8} x2={cx + 2} y2={top + 15} stroke="#4a6fa5" strokeWidth="1.5" />
      </g>
    );
  }
  if (mood === "nasa") {
    return (
      <path
        className="ilo-mood-swirl"
        d={`M ${cx} ${top + 2} q 8 -2 6 -8 q -2 -6 -8 -3 q -5 3 0 6 q 4 2 3 -3`}
        fill="none"
        stroke="#8f5fc9"
        strokeWidth="1.5"
        strokeLinecap="round"
      />
    );
  }
  if (mood === "wawa") {
    return (
      <g fill="#e5b93c" className="ilo-mood-zap">
        <path d={`M ${cx - 10} ${top - 2} l 4 -8 l -1 5 l 4 -1 l -5 9 l 1 -5 Z`} />
        <path d={`M ${cx + 6} ${top - 4} l 4 -8 l -1 5 l 4 -1 l -5 9 l 1 -5 Z`} />
      </g>
    );
  }
  if (mood === "suwi") {
    return (
      <g>
        <Heart x={cx - 8} y={top - 4} scale={0.55} delay="0s" />
        <Heart x={cx + 8} y={top - 8} scale={0.45} delay="0.6s" />
      </g>
    );
  }
  return null;
}

// Floating negation badge above the subject when a verb is negated.
function NegBadge({ cx, top }) {
  return (
    <g transform={`translate(${cx + 26},${top - 14})`}>
      <g className="ilo-badge-float">
        <circle r="10" fill="#fff" stroke={NEG_RED} strokeWidth="2.5" />
        <line x1="-6" y1="6" x2="6" y2="-6" stroke={NEG_RED} strokeWidth="2.5" strokeLinecap="round" />
      </g>
    </g>
  );
}

/* ---------------- entity (subject / object / place / dest) ---------------- */

function Entity({ ent, cx, groundY = GROUND_Y, baseHeight = 110, animClass, label, badge }) {
  if (!ent) return null;
  const glyphKey = PRONOUN_ENTITIES.includes(ent.e) ? "jan" : ent.e;
  const glyph = GLYPHS[glyphKey];
  if (!glyph) return null;
  const color = entColor(ent);
  const h = baseHeight * entScale(ent);
  const scale = h / 100;
  const fan = FAN[Math.min(ent.count || 1, 3)];

  return (
    <g
      className={animClass || undefined}
      style={{ opacity: ent.neg ? 0.3 : 1, transformBox: "fill-box", transformOrigin: "center" }}
      data-entity={ent.e}
    >
      {ent.neg && (
        <ellipse cx={cx} cy={groundY - h * 0.5} rx={h * 0.42} ry={h * 0.56} fill="none" stroke={NEG_RED} strokeWidth="2" strokeDasharray="4 4" />
      )}
      {fan.map((f, i) => {
        const gScale = scale * f.s;
        const tx = cx + f.dx - 50 * gScale;
        const ty = groundY - 88 * gScale;
        return (
          <g key={i} transform={`translate(${tx},${ty}) scale(${gScale})`}>
            <Glyph shapes={glyph.shapes} color={color} />
          </g>
        );
      })}
      <MoodOverlay mood={ent.mood} cx={cx} top={groundY - h - 6} />
      {label && (
        <text x={cx} y={groundY + 16} fontSize="14" textAnchor="middle" fill={DETAIL_DARK}>
          {label}
        </text>
      )}
      {badge}
    </g>
  );
}

// Large, faint backdrop glyph for scene.place, centered behind the stage.
function PlaceBackdrop({ place }) {
  if (!place) return null;
  const glyph = GLYPHS[place.e];
  if (!glyph) return null;
  const color = entColor(place);
  const h = 200;
  const scale = h / 100;
  const cx = 240;
  const tx = cx - 50 * scale;
  const ty = GROUND_Y - 88 * scale;
  return (
    <g opacity="0.25" transform={`translate(${tx},${ty}) scale(${scale})`} data-place={place.e}>
      <Glyph shapes={glyph.shapes} color={color} />
    </g>
  );
}

// Dashed arrow from the subject toward scene.dest (verb "tawa" + dest).
function DestArrow({ x1, y1, x2, y2 }) {
  return (
    <g opacity="0.6" data-dest-arrow="true">
      <line x1={x1} y1={y1} x2={x2 - 14} y2={y2} stroke={DETAIL_DARK} strokeWidth="2" strokeDasharray="5 5" />
      <path d={`M ${x2 - 14} ${y2 - 5} L ${x2} ${y2} L ${x2 - 14} ${y2 + 5} Z`} fill={DETAIL_DARK} />
    </g>
  );
}

// Extra decoration per verb, layered near the subject (and sometimes the
// object). sleep/speak/hearts/sound/listen/draw/gaze get bespoke marks;
// the rest rely on the shared CSS animation classes for a distinct feel.
function VerbFx({ animId, sx, topY, hasObject, objX, eyeY, objEyeY }) {
  if (animId === "sleep") {
    return (
      <text className="ilo-zzz" x={sx + 18} y={topY - 6} fontSize="16" fill={DETAIL_DARK}>
        z z
      </text>
    );
  }
  if (animId === "speak") {
    return (
      <g className="ilo-bubble-pulse" style={{ transformBox: "fill-box", transformOrigin: "center" }}>
        <rect x={sx + 14} y={topY - 26} width={54} height={22} rx={8} fill="#fff" stroke={DETAIL_DARK} strokeWidth="1.5" />
        <path d={`M ${sx + 22} ${topY - 4} l 6 8 l 8 -8 Z`} fill="#fff" stroke={DETAIL_DARK} strokeWidth="1.5" />
        <text x={sx + 41} y={topY - 11} fontSize="11" textAnchor="middle" fill={DETAIL_DARK}>
          toki!
        </text>
      </g>
    );
  }
  if (animId === "gaze" && hasObject) {
    // eye-to-eye, so a big watcher looks DOWN at a small watchee
    return <line x1={sx + 20} y1={eyeY} x2={objX - 20} y2={objEyeY} stroke={DETAIL_DARK} strokeWidth="1.5" strokeDasharray="2 5" opacity="0.6" />;
  }
  if (animId === "listen") {
    return (
      <g className="ilo-ear-fx" opacity="0.7">
        <path d={`M ${sx - 30} ${topY + 18} Q ${sx - 42} ${topY + 18} ${sx - 42} ${topY + 8}`} fill="none" stroke={DETAIL_DARK} strokeWidth="1.5" />
        <path d={`M ${sx - 30} ${topY + 24} Q ${sx - 48} ${topY + 24} ${sx - 48} ${topY + 8}`} fill="none" stroke={DETAIL_DARK} strokeWidth="1.5" />
      </g>
    );
  }
  if (animId === "hearts") {
    return (
      <g data-fx="hearts">
        <Heart x={sx - 10} y={topY - 10} scale={0.5} delay="0s" />
        <Heart x={sx + 16} y={topY - 4} scale={0.4} delay="0.5s" />
      </g>
    );
  }
  if (animId === "sound") {
    return (
      <g data-fx="sound">
        <circle className="ilo-rise-item" style={{ animationDelay: "0s" }} cx={sx - 6} cy={topY - 6} r={4} fill="none" stroke={DETAIL_DARK} strokeWidth="1.5" />
        <circle className="ilo-rise-item" style={{ animationDelay: "0.4s" }} cx={sx + 14} cy={topY} r={3} fill="none" stroke={DETAIL_DARK} strokeWidth="1.5" />
      </g>
    );
  }
  if (animId === "draw") {
    return <line className="ilo-draw-line" x1={sx + 16} y1={topY + 18} x2={sx + 40} y2={topY + 18} stroke={DETAIL_DARK} strokeWidth="2" strokeLinecap="round" />;
  }
  return null;
}

function BigMark({ symbol, x, y }) {
  return (
    <text x={x} y={y} fontSize="46" fontWeight="700" fill={DETAIL_DARK} opacity="0.55" textAnchor="middle" className="ilo-mark-float">
      {symbol}
    </text>
  );
}

/* ---------------- css ---------------- */

const CSS = `
.ilo-chomp { animation: ilo-chomp 1.4s ease-in-out infinite; }
@keyframes ilo-chomp { 0%,100% { transform: translateX(0); } 50% { transform: translateX(6px); } }

.ilo-move { animation: ilo-move 1.2s ease-in-out infinite; }
@keyframes ilo-move { 0%,100% { transform: translate(0,0); } 25% { transform: translate(3px,-2px); } 50% { transform: translate(6px,0); } 75% { transform: translate(3px,-2px); } }

.ilo-sleep { animation: ilo-sleep 2.5s ease-in-out infinite; transform-box: fill-box; transform-origin: center; }
@keyframes ilo-sleep { 0%,100% { transform: scale(1); } 50% { transform: scale(1.04); } }

.ilo-gaze { animation: ilo-gaze 2s ease-in-out infinite; }
@keyframes ilo-gaze { 0%,100% { transform: rotate(0deg); } 50% { transform: rotate(3deg); } }

.ilo-speak { animation: ilo-speak-bob 1.6s ease-in-out infinite; }
@keyframes ilo-speak-bob { 0%,100% { transform: translateY(0); } 50% { transform: translateY(-3px); } }

.ilo-work { animation: ilo-work 0.9s ease-in-out infinite; }
@keyframes ilo-work { 0%,100% { transform: translateY(0); } 50% { transform: translateY(-5px); } }

.ilo-wiggle { animation: ilo-wiggle 1.3s ease-in-out infinite; }
@keyframes ilo-wiggle { 0%,100% { transform: rotate(-4deg); } 50% { transform: rotate(4deg); } }

.ilo-clash { animation: ilo-clash 0.5s ease-in-out infinite; }
@keyframes ilo-clash { 0%,100% { transform: translateX(0); } 25% { transform: translateX(-4px); } 75% { transform: translateX(4px); } }

.ilo-offer { animation: ilo-offer 1.8s ease-in-out infinite; }
@keyframes ilo-offer { 0%,100% { transform: rotate(0deg); } 50% { transform: rotate(6deg); } }

.ilo-hold { animation: ilo-hold 2s ease-in-out infinite; }
@keyframes ilo-hold { 0%,100% { transform: translateY(0); } 50% { transform: translateY(-2px); } }

.ilo-listen { animation: ilo-listen 1.6s ease-in-out infinite; }
@keyframes ilo-listen { 0%,100% { transform: translateY(0); } 50% { transform: translateY(-2px); } }

.ilo-hearts { animation: ilo-hearts-bob 1.8s ease-in-out infinite; }
@keyframes ilo-hearts-bob { 0%,100% { transform: translateY(0); } 50% { transform: translateY(-3px); } }

.ilo-sound { animation: ilo-sound-bob 1.5s ease-in-out infinite; }
@keyframes ilo-sound-bob { 0%,100% { transform: translateY(0); } 50% { transform: translateY(-3px); } }

.ilo-draw { animation: ilo-draw-bob 2s ease-in-out infinite; }
@keyframes ilo-draw-bob { 0%,100% { transform: translateY(0); } 50% { transform: translateY(-2px); } }

.ilo-zzz { animation: ilo-zzz-float 2.2s ease-in-out infinite; }
@keyframes ilo-zzz-float { 0% { opacity: 0; transform: translateY(4px); } 30% { opacity: 1; } 100% { opacity: 0; transform: translateY(-14px); } }

.ilo-bubble-pulse { animation: ilo-bubble-pulse-kf 1.6s ease-in-out infinite; }
@keyframes ilo-bubble-pulse-kf { 0%,100% { transform: scale(1); opacity: 0.9; } 50% { transform: scale(1.06); opacity: 1; } }

.ilo-rise-item { animation: ilo-rise-float 1.8s ease-in-out infinite; }
@keyframes ilo-rise-float { 0% { opacity: 0; transform: translateY(6px); } 40% { opacity: 1; } 100% { opacity: 0; transform: translateY(-16px); } }

.ilo-draw-line { animation: ilo-draw-grow 2s ease-in-out infinite; }
@keyframes ilo-draw-grow { 0% { opacity: 0; } 15% { opacity: 1; } 60% { opacity: 1; } 100% { opacity: 0; } }

.ilo-ear-fx { animation: ilo-ear-pulse 1.6s ease-in-out infinite; }
@keyframes ilo-ear-pulse { 0%,100% { opacity: 0.3; } 50% { opacity: 0.8; } }

.ilo-badge-float { animation: ilo-badge-float-kf 2s ease-in-out infinite; }
@keyframes ilo-badge-float-kf { 0%,100% { transform: translateY(0); } 50% { transform: translateY(-3px); } }

.ilo-mark-float { animation: ilo-mark-float-kf 2.4s ease-in-out infinite; }
@keyframes ilo-mark-float-kf { 0%,100% { transform: translateY(0); } 50% { transform: translateY(-4px); } }

.ilo-sparkle { animation: ilo-sparkle-kf 1.8s ease-in-out infinite; }
@keyframes ilo-sparkle-kf { 0%,100% { transform: scale(0.85); opacity: 0.6; } 50% { transform: scale(1.15); opacity: 1; } }

.ilo-mood-cloud, .ilo-mood-swirl, .ilo-mood-zap { animation: ilo-mood-bob 2.2s ease-in-out infinite; }
@keyframes ilo-mood-bob { 0%,100% { transform: translateY(0); } 50% { transform: translateY(-2px); } }
`;

/* ---------------- standalone glyph icon ---------------- */

// Small self-contained icon for a single word's glyph — used by the
// picture glossary. Not part of the scene-rendering pipeline: no
// positioning, animation, or place backdrop, just the shape at rest.
export function GlyphIcon({ word, size = 44, color }) {
  const glyph = GLYPHS[word];
  if (!glyph) return null;
  const body = color || DEFAULT_BODY;
  return (
    <svg viewBox="0 0 100 100" width={size} height={size} role="img" aria-label={word}>
      <Glyph shapes={glyph.shapes} color={body} />
    </svg>
  );
}

/* ---------------- top-level component ---------------- */

export default function SceneRenderer({ scene, width = 480 }) {
  if (!scene) return null;

  const height = 300;
  const subjectX = 140;
  const objectX = scene.verb === "jo" ? 240 : 330;
  const destX = 370;

  const animId = scene.verb ? ACTIONS[scene.verb] : null;
  const subjAnimClass = !scene.verbNeg && animId ? `ilo-${animId}` : undefined;
  const subjEnt = scene.subject;
  const subjLabel = subjEnt && PRONOUN_ENTITIES.includes(subjEnt.e) ? subjEnt.e : null;
  const subjTop = subjEnt ? GROUND_Y - 110 * entScale(subjEnt) : GROUND_Y - 110;
  // Glyph eyes sit ~30% down from the top of the body, so eye level is
  // ~70% of the entity's scaled height above the ground.
  const eyeAt = (ent) => GROUND_Y - 110 * entScale(ent) * 0.7;
  const subjEyeY = subjEnt ? eyeAt(subjEnt) : GROUND_Y - 77;
  const objEyeY = scene.object ? eyeAt(scene.object) : subjEyeY;

  return (
    <svg
      viewBox={`0 0 480 ${height}`}
      width={width}
      height={Math.round((width * height) / 480)}
      role="img"
      aria-label="toki pona scene"
      data-testid="ilo-scene"
    >
      <style>{CSS}</style>
      <rect x="6" y="6" width="468" height="288" rx="24" fill={STAGE_BG} />
      <rect x="6" y={GROUND_Y} width="468" height={294 - GROUND_Y} fill={GROUND} opacity="0.35" />
      <line x1="20" y1={GROUND_Y} x2="460" y2={GROUND_Y} stroke={GROUND} strokeWidth="3" />

      <PlaceBackdrop place={scene.place} />

      {scene.dest && <Entity ent={scene.dest} cx={destX} baseHeight={90} />}
      {scene.dest && subjEnt && <DestArrow x1={subjectX + 30} y1={GROUND_Y - 20} x2={destX} y2={GROUND_Y - 20} />}

      {scene.object && <Entity ent={scene.object} cx={objectX} />}

      {subjEnt && (
        <Entity
          ent={subjEnt}
          cx={subjectX}
          animClass={subjAnimClass}
          label={subjLabel}
          badge={scene.verbNeg ? <NegBadge cx={subjectX} top={subjTop} /> : null}
        />
      )}

      {animId && !scene.verbNeg && (
        <VerbFx animId={animId} sx={subjectX} topY={subjTop} hasObject={!!scene.object} objX={objectX} eyeY={subjEyeY} objEyeY={objEyeY} />
      )}

      {scene.question && <BigMark symbol="?" x={430} y={54} />}
      {scene.imperative && <BigMark symbol="!" x={scene.question ? 396 : 430} y={54} />}

      {scene.emphasis && (
        <>
          <Star x={26} y={26} scale={1} delay="0s" />
          <Star x={26} y={272} scale={0.8} delay="0.3s" />
          <Star x={454} y={272} scale={0.9} delay="0.6s" />
        </>
      )}
    </svg>
  );
}
